import { EventType, Trait } from '@company/types/workerFilters.type';
import { ApplicantsType } from '@constants/applicantsType';
import type { DirectChatType } from '@constants/directChat';
import Address from '@th-types/address.type';
import { CompanyRating, Reviewer, WorkerRating } from '@th-types/rating.type';
import { IWorkerBasicInfo } from '@th-types/worker.type';

export type { DirectChatType };

export enum ComHubTabs {
  NEW = 'New',
  JOB_MESSAGES = 'Job Messages',
  DIRECT_MESSAGES = 'Direct Messages',
  RECEIVED_REVIEWS = 'Received Reviews',
  RECEIVED_REQUESTS = 'Review Requests',
  REVIEWS_AND_ENDORSEMENTS = 'Reviews & Endorsements Left',
}

export enum ComHubTabKeys {
  NEW = 'new',
  JOB_MESSAGES = 'job-messages',
  DIRECT_MESSAGES = 'direct-messages',
  RECEIVED_REVIEWS = 'received-reviews',
  RECEIVED_REQUESTS = 'received-requests',
  REVIEWS_AND_ENDORSEMENTS = 'reviews-and-endorsements',
}

export const ComHubTabRoutes: Record<
  string,
  { label: ComHubTabs; key: string }
> = {
  [ComHubTabs.NEW]: { label: ComHubTabs.NEW, key: 'new' },
  [ComHubTabs.JOB_MESSAGES]: {
    label: ComHubTabs.JOB_MESSAGES,
    key: ComHubTabKeys.JOB_MESSAGES,
  },
  [ComHubTabs.DIRECT_MESSAGES]: {
    label: ComHubTabs.DIRECT_MESSAGES,
    key: ComHubTabKeys.DIRECT_MESSAGES,
  },
  [ComHubTabs.RECEIVED_REVIEWS]: {
    label: ComHubTabs.RECEIVED_REVIEWS,
    key: ComHubTabKeys.RECEIVED_REVIEWS,
  },
  [ComHubTabs.RECEIVED_REQUESTS]: {
    label: ComHubTabs.RECEIVED_REQUESTS,
    key: ComHubTabKeys.RECEIVED_REQUESTS,
  },
  [ComHubTabs.REVIEWS_AND_ENDORSEMENTS]: {
    label: ComHubTabs.REVIEWS_AND_ENDORSEMENTS,
    key: ComHubTabKeys.REVIEWS_AND_ENDORSEMENTS,
  },
};

export type ComHubTabRoutesType =
  (typeof ComHubTabRoutes)[keyof typeof ComHubTabRoutes];

export interface GigInfoPreviewData {
  brandRepresented: string;
  companyName: string;
  eventName: string;
  jobTitle: string;
  workedDays: string;
  workerName: string;
}

export interface DismissItemsType {
  items: Array<{
    associatedType: UserActionAssociatedTypeEnum;
    associatedIds: number[];
  }>;
}

export interface WorkInfo {
  workedDays: number;
  start: string;
  end: string;
  position: string;
  workerId: number;
  workerName: string;
}

interface User {
  id: number;
  firstName: string;
  lastName: string;
  fullName: string;
  profileImageUrl: string;
  address: Record<string, any>;
  cellPhone: string;
  email: string;
}

export interface UserState {
  id: number;
  dismissed: string;
  associatedType: string;
  associatedId: number;
  dateCreated: string;
  lastUpdated: string;
  new: boolean;
  type: string;
  // user: User;
  userId: number;
}

export interface ReviewMetadata {
  jobAssignmentId: number;
  eventName: string;
  workerId: number | null;
  workerFullName: string | null;
  jobId: number;
  completed?: boolean;
  reviewer?: Reviewer;
  reviewType: PendingReviewModelType;
}

export interface Review extends ReviewMetadata {
  companyRating: CompanyRating;
  workInfo: WorkInfo;
  userState?: UserState;
}

interface Pagination {
  max: number;
  offset: number;
  count: number;
}

export interface ReceivedReviewsResponse {
  reviews: Review[];
  pagination: Pagination;
}

export interface ReviewRequestsResponse {
  ratingRequests: Review[];
  pagination: Pagination;
}
export interface GivenReview {
  jobAssignmentId: number;
  endorsementCaseId: number;
  eventName: string;
  workerId: number;
  jobId: number;
  type: GivenReviewTypeEnum;
  workerRating: WorkerRating | null;
  endorsementCase?: ComHubEndorsementCase;
  workInfo: WorkInfo;
  userState: UserState;
}

export interface ReviewsLeftResponse {
  reviews: GivenReview[];
  pagination: Pagination;
}

interface Brand {
  id: number;
  name: string;
  premium: boolean;
  profileURLName: string;
  profileImageUrl: string;
  rating: number | null;
  reviewsCount: number;
  showSearchPosition: boolean;
  isValidatedCompany: boolean;
}

export interface Endorsement {
  id: number;
  isEndorsedManager: boolean;
  workerRating: WorkerRating | null;
  endorser: User;
  endorsedWorker: User;
  sharedEventsNumber: string;
  sharedEventsType: EventType[];
  endorsedTraits: Trait[];
  brandWorked: Brand;
  reviewDescription: string | null;
  performance: number;
  dateCreated: string;
  photoIsRepresentative: boolean;
}

export interface ComHubEndorsementCase {
  id: number;
  performance: number;
  dateCreated: string;
  reviewDescription: string;
  photoIsRepresentative: boolean;
  endorser: User;
  endorsedWorker: User;
}

export enum ComHubHeadersEnum {
  ACTIONS_COLUMN = '',
  AUTHOR = 'AUTHOR',
  DATES_WORKED = 'DATES WORKED',
  EVENT_NAME = 'EVENT NAME',
  JOB_DATES = 'JOB DATES',
  POSITION = 'POSITION',
  RATING = 'RATING',
  REPLIED = 'REPLIED',
  RESPONDED = 'RESPONDED',
  LOCATION = 'LOCATION',
  SUBJECT = 'SUBJECT',
  CHAT_NAME = 'CHAT NAME',
  WORKER_NAME = 'WORKER NAME',
  DISTANCE = 'DISTANCE',
  BRAND_REPRESENTED = 'BRAND REPRESENTED',
  ADDRESS = 'ADDRESS',
  START = 'START',
  END = 'END',
  TH_GIG = 'TH GIG',
  WORKER_REVIEWED_DATE = 'WORKER REVIEWED DATE',
}

export enum ComHubQueryParamsEnum {
  UNREAD = 'searchUnread',
  NEW = 'searchNew',
  SEARCH_TEXT = 'searchText',
  DATE_FROM = 'dateFrom',
  DATE_TO = 'dateTo',
  MAX = 'max',
  OFFSET = 'offset',
  MODE = 'mode',
}

export enum ChatModeEnum {
  INBOX = 'inbox',
  SENT = 'sent',
}

export enum UserActionAssociatedTypeEnum {
  COMPANY_RATING = 'COMPANY_RATING',
  RATING_REQUEST = 'RATING_REQUEST',
  DIRECT_CHAT = 'DIRECT_CHAT',
  JOB_CHAT = 'JOB_CHAT',
  JOB_APPLICATIONS = 'JOB_APPLICATIONS',
}

export enum ComHubChatTypeEnum {
  DIRECT_CHAT = 'DIRECT_CHAT',
  JOB_CHAT = 'JOB_CHAT',
}

export enum ReviewTypeEnum {
  RECEIVED_REVIEW = 'RECEIVED_REVIEW',
  REVIEW_REQUEST = 'REVIEW_REQUEST',
  GIVEN_REVIEW = 'GIVEN_REVIEW',
  GIVEN_ENDORSEMENT = 'GIVEN_ENDORSEMENT',
  REVIEW_LEFT = 'REVIEW_LEFT',
}

export enum GivenReviewTypeEnum {
  RATING = 'RATING',
  ENDORSEMENT_CASE = 'ENDORSEMENT_CASE',
}

export enum PendingReviewModelType {
  REVIEW_REQUEST = 'REVIEW_REQUEST',
  TH_GIG_PENDING_REVIEW = 'TH_GIG_PENDING_REVIEW',
}
/**
 * This type is for individual direct message, but is not used yet
 */
// export interface DirectChat {
//   chatId: number;
//   worker: {
//     id: number;
//     fullName: string;
//   };
//   member: {
//     id: number;
//     fullName: string;
//   };
//   userState: UserState;
//   unreadMessages: number;
//   lastMessageDateTime: string;
//   subject: string;
// }

export interface DirectMessage {
  chatId: number;
  workerId: number | null;
  workerFullName: string;
  hasMultipleAuthors: boolean;
  unreadMessages: number;
  messageCount: number;
  lastMessageDateTime: string;
  lastMessageAuthorName: string;
  subject: string;
  firstUnreadMessageId?: number | null;
  /** DIRECT conversation or read-only platform ANNOUNCEMENT. Defaults to DIRECT when omitted. */
  type?: DirectChatType;
}

export interface DirectMessagesResponse {
  directChats: DirectMessage[];
  pagination: Pagination;
}

/**
 * This types are for individual job message, but is not used yet
 */
/*
interface JobAssignmentInfo {
  eventName: string;
  jobId: number;
  start: string;
  end: string;
  position: string;
  address: Record<string, any>;
}

interface JobChatWorker {
  id: number;
  firstName: string;
  lastName: string;
  fullName: string;
  profileImageUrl: string;
  address: Record<string, any>;
  cellPhone: string;
  email: string;
}

interface JobChatMember {
  id: number;
  firstName: string;
  lastName: string;
  fullName: string;
  profileImageUrl: string;
  address: Record<string, any>;
  cellPhone: string;
  email: string;
}

interface JobChatMessage {
  id: number;
  text: string;
  dateCreated: string;
  author: JobChatWorker | JobChatMember;
}

export interface JobMessage {
  jobAssignmentId: number;
  jobAssignmentInfo: JobAssignmentInfo;
  worker: JobChatWorker;
  member: JobChatMember;
  userState: UserState;
  unreadMessages: number;
  lastMessageDateTime: string;
  messages: JobChatMessage[];
}
*/
export interface JobChat {
  chatId: number;
  jobAssignmentInfo: {
    address: Address;
    end: string;
    eventName: string;
    jobAssignmentId: number;
    jobId: number;
    position: string;
    start: string;
    status: ApplicantsType;
  };
  hasMultipleAuthors: boolean;
  lastMessageDateTime: string;
  messageCount: number;
  unreadMessages: number;
  firstUnreadMessageId?: number | null;
  workerInfo: IWorkerBasicInfo;
}

export interface JobChatResponse {
  jobChats: JobChat[];
  pagination: Pagination;
}

export type WorkerModalParams = {
  workerId: number;
  jobAssignmentId?: number;
  jobId?: number;
};

export enum CounterType {
  JOB_CHATS = 'jobChats',
  DIRECT_MESSAGES = 'directMessages',
  RECEIVED_REVIEWS = 'receivedReviews',
  REVIEW_REQUESTS = 'reviewRequests',
}

export interface MarkAllJobChatsAsReadResponse {
  success: boolean;
  message?: string;
  error?: string;
  data?: {
    chatsUpdated: number;
    cutoffDate: string;
    durationMs: number;
  };
}

export interface MarkAllDirectChatsAsReadResponse {
  success: boolean;
  message?: string;
  error?: string;
  data?: {
    chatsUpdated: number;
    cutoffDate: string;
    durationMs: number;
  };
}
